import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Edit2, Search } from "lucide-react";

const empty = {
  name: "", description: "", category: "dev",
  type: "one_off", billing_cycle: "month",
  unit_price: 0, purchase_price: 0, supplier: "",
  default_qty: 1, vat_rate: 20, active: true,
};

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/services").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Nom requis");
    try {
      const payload = {
        ...form,
        unit_price: parseFloat(form.unit_price) || 0,
        purchase_price: parseFloat(form.purchase_price) || 0,
        default_qty: parseFloat(form.default_qty) || 1,
        vat_rate: parseFloat(form.vat_rate) || 20,
      };
      if (editing) await api.patch(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      setOpen(false); setEditing(null); setForm(empty); load();
      toast.success("Enregistré");
    } catch { toast.error("Erreur"); }
  };

  const edit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/services/${id}`); load(); };
  const toggle = async (id, active) => { await api.patch(`/admin/services/${id}`, { active }); load(); };

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((it) => (it.name || "").toLowerCase().includes(s) || (it.supplier || "").toLowerCase().includes(s) || (it.category || "").toLowerCase().includes(s));
  }, [items, q]);

  const totals = useMemo(() => {
    let revenue = 0, cost = 0;
    items.forEach((it) => {
      const qty = Number(it.default_qty || 1);
      revenue += Number(it.unit_price || 0) * qty;
      cost += Number(it.purchase_price || 0) * qty;
    });
    return { revenue, cost, margin: revenue - cost };
  }, [items]);

  return (
    <div data-testid="admin-services">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Catalogue produits & services</h1>
          <p className="text-sm text-[#64748B] mt-1">Prix d'achat, prix de vente HT/TTC, marge, fournisseur. Réutilisable dans tous vos devis.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button data-testid="service-add-btn" className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold"><Plus size={16} className="mr-1.5" />Nouveau produit / service</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouveau"} produit / service</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom *</Label><Input data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Catégorie</Label>
                  <select className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" value={form.category || "dev"} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="accompagnement">Accompagnement</option><option value="dev">Développement</option><option value="identite">Identité</option><option value="recurrent">Récurrent</option><option value="produit">Produit</option>
                  </select>
                </div>
                <div><Label>Type</Label>
                  <select className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="one_off">Ponctuel</option><option value="recurring">Récurrent</option>
                  </select>
                </div>
              </div>
              {form.type === "recurring" && (
                <div><Label>Cycle de facturation</Label>
                  <select className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" value={form.billing_cycle} onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}>
                    <option value="month">Mensuel</option><option value="year">Annuel</option>
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 bg-[#FAFAFA] p-3 rounded-xl border border-black/8">
                <div><Label className="text-xs">💰 Prix d'achat HT (€)</Label><Input data-testid="service-purchase" type="number" step="0.01" value={form.purchase_price || 0} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} /></div>
                <div><Label className="text-xs">🏢 Fournisseur</Label><Input data-testid="service-supplier" value={form.supplier || ""} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Optionnel" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3 bg-[#06B6D4]/5 p-3 rounded-xl border border-[#06B6D4]/20">
                <div><Label className="text-xs">💵 Prix de vente HT (€)</Label><Input data-testid="service-price" type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></div>
                <div><Label className="text-xs">Qté défaut</Label><Input type="number" value={form.default_qty} onChange={(e) => setForm({ ...form, default_qty: e.target.value })} /></div>
                <div><Label className="text-xs">TVA (%)</Label><Input type="number" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="service-save" onClick={save} className="bg-[#0A1128] text-white">Enregistrer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3" data-testid="service-totals">
        <Tile label="CA potentiel HT" value={`${fmt(totals.revenue)} €`} color="#06B6D4" />
        <Tile label="Coût total HT" value={`${fmt(totals.cost)} €`} color="#FFDCA8" />
        <Tile label="Marge brute" value={`${fmt(totals.margin)} €`} color="#A7F3D0" sub={totals.revenue > 0 ? `${Math.round((totals.margin / totals.revenue) * 100)}%` : "—"} />
      </div>

      <div className="mt-3 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher nom, fournisseur, catégorie…" className="pl-9" data-testid="service-search" />
      </div>

      <div className="mt-3 bg-white border border-black/8 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="services-table">
          <thead className="bg-[#FAFAFA] border-b border-black/8 text-[10px] font-bold tracking-widest uppercase text-[#64748B]">
            <tr>
              <th className="text-left px-3 py-2.5">Produit / Service</th>
              <th className="text-left px-3 py-2.5">Fournisseur</th>
              <th className="text-right px-3 py-2.5">Prix achat HT</th>
              <th className="text-right px-3 py-2.5">Vente HT</th>
              <th className="text-right px-3 py-2.5">TVA</th>
              <th className="text-right px-3 py-2.5">Vente TTC</th>
              <th className="text-right px-3 py-2.5">Marge HT</th>
              <th className="text-center px-3 py-2.5">Actif</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center text-sm text-[#64748B] py-8">{items.length === 0 ? "Aucun produit / service. Cliquez \"Nouveau\" pour démarrer." : "Aucun résultat pour cette recherche."}</td></tr>
            )}
            {filtered.map((s) => {
              const ht = Number(s.unit_price || 0);
              const tva = Number(s.vat_rate || 0);
              const ttc = ht * (1 + tva / 100);
              const cost = Number(s.purchase_price || 0);
              const margin = ht - cost;
              const marginPct = ht > 0 ? Math.round((margin / ht) * 100) : 0;
              return (
                <tr key={s.id} data-testid={`service-${s.id}`} className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA] transition-colors">
                  <td className="px-3 py-3">
                    <button onClick={() => edit(s)} className="display font-extrabold text-[#0A1128] hover:underline text-left" data-testid={`service-edit-${s.id}`}>{s.name}</button>
                    <div className="text-[10px] text-[#64748B] mt-0.5">
                      <span className="inline-block px-1.5 py-0.5 rounded bg-black/5 mr-1">{s.category || "—"}</span>
                      {s.type === "recurring" ? <span className="text-[#06B6D4] font-bold">↻ Récurrent {s.billing_cycle === "year" ? "annuel" : "mensuel"}</span> : <span className="text-[#94a3b8]">Ponctuel</span>}
                    </div>
                    {s.description && <div className="text-[11px] text-[#64748B] mt-0.5 line-clamp-1 max-w-md">{s.description}</div>}
                  </td>
                  <td className="px-3 py-3 text-xs text-[#475569]">{s.supplier || <span className="text-[#cbd5e1]">—</span>}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#64748B] tabular-nums">{cost > 0 ? `${fmt(cost)} €` : "—"}</td>
                  <td className="px-3 py-3 text-right font-extrabold text-[#0A1128] tabular-nums">{fmt(ht)} €</td>
                  <td className="px-3 py-3 text-right text-xs text-[#64748B] tabular-nums">{tva}%</td>
                  <td className="px-3 py-3 text-right font-bold text-[#06B6D4] tabular-nums">{fmt(ttc)} €</td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {cost > 0 ? (
                      <div>
                        <span className={`font-bold ${margin >= 0 ? "text-green-600" : "text-red-600"}`}>{fmt(margin)} €</span>
                        <div className="text-[10px] text-[#64748B]">{marginPct}%</div>
                      </div>
                    ) : <span className="text-[#cbd5e1]">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <Switch checked={s.active !== false} onCheckedChange={(v) => toggle(s.id, v)} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => edit(s)} className="p-1.5 text-[#64748B] hover:text-[#06B6D4]" title="Modifier"><Edit2 size={14} /></button>
                      <button onClick={() => del(s.id)} className="p-1.5 text-[#94a3b8] hover:text-red-500" title="Supprimer"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, color, sub }) {
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 h-full w-1" style={{ background: color }} />
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] pl-1">{label}</div>
      <div className="display text-2xl font-extrabold text-[#0A1128] mt-1 tabular-nums pl-1">{value}</div>
      {sub && <div className="text-[11px] text-[#64748B] pl-1">{sub}</div>}
    </div>
  );
}
